import { useDeferredValue, useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock3,
  LifeBuoy,
  Loader2,
  MessageSquareText,
  Plus,
  Search,
  Send,
  ShieldCheck,
  User2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../auth/useAuth";
import {
  createSupportCase,
  getSupportCase,
  getSupportCases,
  replySupportCase,
  type SupportCaseCategory,
  type SupportCaseDetail,
  type SupportCaseListItem,
  type SupportCaseMessage,
  type SupportCaseStatus,
} from "../api/support";
import { getWorkspaceSites } from "../api/workspace";
import { formatDate, formatRelativeTime } from "../lib/formatDate";

const STATUS_FILTERS = [
  { value: "ALL", label: "All cases" },
  { value: "OPEN", label: "Open" },
  { value: "PENDING_CUSTOMER", label: "Waiting on you" },
  { value: "PENDING_INTERNAL", label: "Waiting on StayLayer" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "CLOSED", label: "Closed" },
] as const;

const CATEGORY_OPTIONS: Array<{
  value: SupportCaseCategory;
  label: string;
}> = [
  { value: "OTHER", label: "General help" },
  { value: "BILLING", label: "Billing" },
  { value: "DEPLOYMENT", label: "Deployments" },
  { value: "DOMAIN", label: "Domains" },
  { value: "FORMS", label: "Inquiries & forms" },
  { value: "SEO", label: "SEO" },
  { value: "TRANSLATION", label: "Translation" },
  { value: "ACCESS", label: "Access" },
  { value: "CONTENT", label: "Content" },
  { value: "ACCOUNT", label: "Account" },
];

const STATUS_STYLES: Record<SupportCaseStatus, string> = {
  OPEN: "border-sky-200 bg-sky-50 text-sky-700",
  PENDING_CUSTOMER: "border-amber-200 bg-amber-50 text-amber-700",
  PENDING_INTERNAL: "border-violet-200 bg-violet-50 text-violet-700",
  RESOLVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  CLOSED: "border-slate-200 bg-slate-100 text-slate-600",
};

const CATEGORY_LABELS: Record<SupportCaseCategory, string> = {
  BILLING: "Billing",
  DEPLOYMENT: "Deployment",
  DOMAIN: "Domain",
  FORMS: "Forms",
  SEO: "SEO",
  TRANSLATION: "Translation",
  ACCESS: "Access",
  CONTENT: "Content",
  ACCOUNT: "Account",
  OTHER: "General",
};

type StatusFilterValue = (typeof STATUS_FILTERS)[number]["value"];

function readApiMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  );
}

function statusLabel(status: SupportCaseStatus): string {
  return (
    STATUS_FILTERS.find((option) => option.value === status)?.label ?? status
  );
}

function requesterLabel(
  supportCase: SupportCaseListItem | SupportCaseDetail,
): string {
  return (
    supportCase.requesterUser?.email ??
    supportCase.requesterEmail ??
    "Workspace member"
  );
}

function StatusBadge({ status }: { status: SupportCaseStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${STATUS_STYLES[status]}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function MessageBubble({ message }: { message: SupportCaseMessage }) {
  const isOperator = message.authorIsOperator;
  const title = isOperator
    ? "StayLayer Support"
    : (message.author?.email ?? "Workspace member");
  const subtitle = isOperator
    ? (message.author?.email ?? "Operator")
    : "Workspace reply";

  return (
    <div className={`flex ${isOperator ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-3xl rounded-3xl border px-4 py-3 shadow-sm ${
          isOperator
            ? "border-slate-200 bg-white text-slate-800"
            : "border-[#12392f] bg-[#12392f] text-white"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p
              className={`text-sm font-semibold ${isOperator ? "text-slate-900" : "text-white"}`}
            >
              {title}
            </p>
            <p
              className={`text-xs ${isOperator ? "text-slate-500" : "text-emerald-100/80"}`}
            >
              {subtitle}
            </p>
          </div>
          <span
            className={`text-[11px] ${isOperator ? "text-slate-400" : "text-emerald-100/70"}`}
          >
            {formatDate(message.createdAt)}
          </span>
        </div>
        <p
          className={`mt-3 whitespace-pre-wrap text-sm leading-6 ${isOperator ? "text-slate-700" : "text-white"}`}
        >
          {message.body}
        </p>
      </div>
    </div>
  );
}

function CaseCardSkeleton() {
  return (
    <div className="animate-pulse rounded-3xl border border-slate-100 bg-slate-50 px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-3 w-8 rounded-full bg-slate-200" />
            <div className="h-5 w-20 rounded-full bg-slate-200" />
          </div>
          <div className="h-4 w-4/5 rounded-md bg-slate-200" />
          <div className="h-3 w-1/2 rounded-md bg-slate-200" />
        </div>
        <div className="h-3 w-10 shrink-0 rounded-md bg-slate-200" />
      </div>
      <div className="mt-3 flex gap-2">
        <div className="h-5 w-16 rounded-full bg-slate-200" />
      </div>
    </div>
  );
}

export default function SupportPage() {
  const { session } = useAuth();
  const tenantId = session?.activeTenant?.id ?? null;
  const activeSiteId = session?.activeSite?.id ?? "";
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCaseId = searchParams.get("caseId");

  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("ALL");
  const [inboxPage, setInboxPage] = useState(1);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [createForm, setCreateForm] = useState<{
    subject: string;
    message: string;
    category: SupportCaseCategory;
    siteId: string;
  }>({
    subject: "",
    message: "",
    category: "OTHER",
    siteId: activeSiteId,
  });

  const sitesQuery = useQuery({
    queryKey: ["workspace-sites", tenantId],
    queryFn: () => getWorkspaceSites(tenantId!),
    enabled: Boolean(tenantId),
    retry: false,
  });

  const casesQuery = useQuery({
    queryKey: [
      "support-cases",
      tenantId,
      statusFilter,
      deferredSearchQuery,
      inboxPage,
    ],
    queryFn: () =>
      getSupportCases(tenantId!, {
        status:
          statusFilter === "ALL"
            ? undefined
            : [statusFilter as SupportCaseStatus],
        q: deferredSearchQuery.trim() || undefined,
        page: inboxPage,
        limit: 20,
      }),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const selectedSummary =
    casesQuery.data?.data.find(
      (supportCase) => supportCase.id === selectedCaseId,
    ) ?? null;

  const detailQuery = useQuery({
    queryKey: ["support-case", tenantId, selectedCaseId],
    queryFn: () => getSupportCase(tenantId!, selectedCaseId!),
    enabled: Boolean(tenantId && selectedCaseId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    setInboxPage(1);
  }, [tenantId]);

  useEffect(() => {
    const total = casesQuery.data?.total ?? 0;
    const limit = casesQuery.data?.limit ?? 20;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    if (inboxPage > totalPages) {
      setInboxPage(totalPages);
    }
  }, [casesQuery.data, inboxPage]);

  useEffect(() => {
    const cases = casesQuery.data?.data ?? [];
    if (cases.length === 0) {
      if (selectedCaseId && detailQuery.isError) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (!selectedCaseId) {
      setSearchParams({ caseId: cases[0].id }, { replace: true });
      return;
    }

    if (
      !cases.some((supportCase) => supportCase.id === selectedCaseId) &&
      detailQuery.isError
    ) {
      setSearchParams({ caseId: cases[0].id }, { replace: true });
    }
  }, [casesQuery.data, detailQuery.isError, selectedCaseId, setSearchParams]);

  const createMutation = useMutation({
    mutationFn: () =>
      createSupportCase(tenantId!, {
        subject: createForm.subject.trim(),
        message: createForm.message.trim(),
        category: createForm.category,
        ...(createForm.siteId ? { siteId: createForm.siteId } : {}),
      }),
    onSuccess: (supportCase) => {
      toast.success(`Case #${supportCase.number} created.`);
      setStatusFilter("ALL");
      setSearchQuery("");
      setInboxPage(1);
      setComposeOpen(false);
      setSearchParams({ caseId: supportCase.id }, { replace: false });
      setReplyBody("");
      setCreateForm((current) => ({
        ...current,
        subject: "",
        message: "",
      }));
      void queryClient.invalidateQueries({
        queryKey: ["support-cases", tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["support-case", tenantId, supportCase.id],
      });
    },
    onError: (error) => {
      toast.error(readApiMessage(error, "Unable to create that support case."));
    },
  });

  const replyMutation = useMutation({
    mutationFn: () =>
      replySupportCase(tenantId!, selectedCaseId!, { body: replyBody.trim() }),
    onSuccess: (supportCase) => {
      toast.success(
        supportCase.status === "OPEN"
          ? "Reply sent. Case reopened."
          : "Reply sent.",
      );
      setReplyBody("");
      void queryClient.invalidateQueries({
        queryKey: ["support-cases", tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["support-case", tenantId, supportCase.id],
      });
    },
    onError: (error) => {
      toast.error(readApiMessage(error, "Unable to send that reply."));
    },
  });

  if (!tenantId) {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-sm text-slate-600">
          Select a workspace to open the shared support inbox.
        </p>
      </div>
    );
  }

  const availableSites = (sitesQuery.data ?? []).filter(
    (site) => site.status !== "ARCHIVED",
  );
  const cases = casesQuery.data?.data ?? [];
  const selectedCase = detailQuery.data ?? selectedSummary;
  const totalCases = casesQuery.data?.total ?? 0;
  const currentLimit = casesQuery.data?.limit ?? 20;
  const totalPages = Math.max(1, Math.ceil(totalCases / currentLimit));
  const pageStart = totalCases === 0 ? 0 : (inboxPage - 1) * currentLimit + 1;
  const pageEnd =
    totalCases === 0 ? 0 : Math.min(totalCases, inboxPage * currentLimit);
  const hasPreviousPage = inboxPage > 1;
  const hasNextPage = inboxPage < totalPages;
  const replyDisabled =
    !selectedCaseId ||
    !replyBody.trim() ||
    replyMutation.isPending ||
    selectedCase?.status === "CLOSED";

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* ── Hero header ── */}
      <section className="rounded-3xl border border-slate-200 bg-[linear-gradient(135deg,rgba(255,255,255,1),rgba(248,250,252,0.98),rgba(219,234,254,0.45))] p-5 shadow-sm lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-700">
              <LifeBuoy className="h-3.5 w-3.5" />
              Workspace support
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 lg:text-3xl">
              Shared support inbox
            </h1>
            <p className="mt-1.5 max-w-xl text-sm leading-6 text-slate-500">
              Every workspace member sees the same threads — submit requests,
              track progress, and keep support in one place.
            </p>
          </div>
          <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-600 shadow-sm sm:flex">
            <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
            <div>
              <p className="font-semibold text-slate-900">Shared visibility</p>
              <p className="text-xs text-slate-500">
                {session?.activeTenant?.name ?? "Your workspace"} · all members
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main two-column layout ── */}
      <div className="grid gap-4 lg:gap-6 lg:grid-cols-[22rem_minmax(0,1fr)] xl:grid-cols-[26rem_minmax(0,1fr)]">
        {/* ── LEFT: Composer + Inbox ── */}
        <div
          className={`flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start ${
            selectedCaseId ? "hidden lg:flex" : "flex"
          }`}
        >
          {/* Collapsible new-case composer */}
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setComposeOpen((open) => !open)}
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-slate-50/60"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
                  <Plus className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Open a new case
                  </p>
                  <p className="text-xs text-slate-500">
                    Submit a workspace support request
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
                  composeOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {composeOpen && (
              <div className="border-t border-slate-100 px-5 pb-5 pt-4">
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Subject
                    <input
                      type="text"
                      value={createForm.subject}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          subject: event.target.value,
                        }))
                      }
                      placeholder="Summarize the issue or request"
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none"
                    />
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Category
                      <select
                        value={createForm.category}
                        onChange={(event) =>
                          setCreateForm((current) => ({
                            ...current,
                            category: event.target.value as SupportCaseCategory,
                          }))
                        }
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none"
                      >
                        {CATEGORY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block text-sm font-medium text-slate-700">
                      Site
                      <select
                        value={createForm.siteId}
                        onChange={(event) =>
                          setCreateForm((current) => ({
                            ...current,
                            siteId: event.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none"
                      >
                        <option value="">Entire workspace</option>
                        {availableSites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="block text-sm font-medium text-slate-700">
                    Message
                    <textarea
                      rows={5}
                      value={createForm.message}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          message: event.target.value,
                        }))
                      }
                      placeholder="Describe what happened, what you expected, and anything we should review."
                      className="mt-1 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => createMutation.mutate()}
                    disabled={
                      createMutation.isPending ||
                      createForm.subject.trim().length < 3 ||
                      createForm.message.trim().length === 0
                    }
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Open support case
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Inbox */}
          <section className="flex flex-col rounded-[28px] border border-slate-200 bg-white shadow-sm">
            {/* Inbox header */}
            <div className="shrink-0 border-b border-slate-100 px-5 pb-4 pt-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-slate-950">
                  Shared inbox
                </h2>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  {totalCases} {totalCases === 1 ? "case" : "cases"}
                </span>
              </div>

              {/* Search with clear button */}
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setInboxPage(1);
                  }}
                  placeholder="Search cases…"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setInboxPage(1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Status filters — horizontally scrollable */}
              <div className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5">
                {STATUS_FILTERS.map((option) => {
                  const active = statusFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setStatusFilter(option.value);
                        setInboxPage(1);
                      }}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                        active
                          ? "bg-[#12392f] text-white shadow-sm"
                          : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Case list — scrollable */}
            <div className="min-h-0 space-y-2 overflow-y-auto p-3 lg:max-h-144">
              {casesQuery.isLoading && !casesQuery.data ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <CaseCardSkeleton key={index} />
                ))
              ) : casesQuery.isError ? (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                  {readApiMessage(
                    casesQuery.error,
                    "Unable to load the support inbox.",
                  )}
                </div>
              ) : cases.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-200 px-4 py-10 text-center">
                  <MessageSquareText className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-700">
                    No cases match this filter
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Open a new case above or widen the search.
                  </p>
                </div>
              ) : (
                cases.map((supportCase) => {
                  const active = supportCase.id === selectedCaseId;
                  return (
                    <button
                      key={supportCase.id}
                      type="button"
                      onClick={() =>
                        setSearchParams(
                          { caseId: supportCase.id },
                          { replace: false },
                        )
                      }
                      className={`w-full rounded-3xl border px-4 py-3.5 text-left transition ${
                        active
                          ? "border-[#12392f] bg-[#12392f] text-white shadow-[0_12px_28px_rgba(18,57,47,0.20)]"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                                active
                                  ? "text-emerald-100/80"
                                  : "text-slate-400"
                              }`}
                            >
                              #{supportCase.number}
                            </span>
                            <StatusBadge status={supportCase.status} />
                          </div>
                          <p
                            className={`mt-1.5 truncate text-sm font-semibold leading-snug ${
                              active ? "text-white" : "text-slate-900"
                            }`}
                          >
                            {supportCase.subject}
                          </p>
                          <p
                            className={`mt-1 truncate text-xs ${
                              active ? "text-emerald-100/75" : "text-slate-500"
                            }`}
                          >
                            {requesterLabel(supportCase)}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 whitespace-nowrap text-[11px] ${
                            active ? "text-emerald-100/70" : "text-slate-400"
                          }`}
                        >
                          {formatRelativeTime(supportCase.lastActivityAt)}
                        </span>
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span
                          className={`rounded-full px-2 py-0.5 font-semibold ${
                            active
                              ? "bg-white/12 text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {CATEGORY_LABELS[supportCase.category]}
                        </span>
                        {supportCase.site ? (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                              active
                                ? "bg-white/12 text-white"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            <Building2 className="h-2.5 w-2.5" />
                            {supportCase.site.name}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Pagination footer */}
            <div className="shrink-0 border-t border-slate-100 px-4 py-3">
              <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                <p className="font-medium">
                  {totalCases === 0
                    ? "No cases"
                    : `${pageStart}–${pageEnd} of ${totalCases}`}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-0.5 rounded-xl border border-slate-200 bg-white p-1">
                    <button
                      type="button"
                      onClick={() => setInboxPage(1)}
                      disabled={!hasPreviousPage}
                      className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                      aria-label="First page"
                    >
                      <ChevronsLeft className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setInboxPage((current) => Math.max(1, current - 1))
                      }
                      disabled={!hasPreviousPage}
                      className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <span className="min-w-14 px-2 text-center text-[11px] font-semibold text-slate-700">
                      {inboxPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setInboxPage((current) =>
                          Math.min(totalPages, current + 1),
                        )
                      }
                      disabled={!hasNextPage}
                      className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setInboxPage(totalPages)}
                      disabled={!hasNextPage}
                      className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                      aria-label="Last page"
                    >
                      <ChevronsRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* ── RIGHT: Thread detail ── */}
        <section
          className={`min-w-0 rounded-[28px] border border-slate-200 bg-white shadow-sm ${
            !selectedCaseId ? "hidden lg:block" : "block"
          }`}
        >
          {/* Mobile: back to inbox */}
          {selectedCaseId && (
            <div className="border-b border-slate-100 px-5 py-4 lg:hidden">
              <button
                type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to inbox
              </button>
            </div>
          )}

          <div className="p-5 lg:p-6">
            {!selectedCaseId ? (
              <div className="flex min-h-96 flex-col items-center justify-center text-center">
                <LifeBuoy className="h-10 w-10 text-slate-300" />
                <h2 className="mt-4 text-xl font-semibold text-slate-900">
                  Select a support case
                </h2>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Pick a thread from the shared inbox, or open a new case from
                  the composer on the left.
                </p>
              </div>
            ) : detailQuery.isLoading && !selectedCase ? (
              <div className="flex min-h-96 items-center justify-center text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading support thread…
              </div>
            ) : detailQuery.isError ? (
              <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                {readApiMessage(
                  detailQuery.error,
                  "Unable to load this support thread.",
                )}
              </div>
            ) : selectedCase ? (
              <div className="space-y-6">
                <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-5">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                        Case #{selectedCase.number}
                      </span>
                      <StatusBadge status={selectedCase.status} />
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950 lg:text-2xl">
                      {selectedCase.subject}
                    </h2>
                    <p className="mt-2 text-sm text-slate-500">
                      {CATEGORY_LABELS[selectedCase.category]} case opened by{" "}
                      {requesterLabel(selectedCase)}.
                    </p>
                  </div>

                  <div className="grid gap-2 text-sm text-slate-600 sm:text-right">
                    <div className="inline-flex items-center gap-2 sm:justify-end">
                      <Clock3 className="h-4 w-4 text-slate-400" />
                      Last activity{" "}
                      {formatRelativeTime(selectedCase.lastActivityAt)}
                    </div>
                    {selectedCase.assignedOperator ? (
                      <div className="inline-flex items-center gap-2 sm:justify-end">
                        <ShieldCheck className="h-4 w-4 text-slate-400" />
                        Assigned to {selectedCase.assignedOperator.email}
                      </div>
                    ) : null}
                  </div>
                </header>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Requester
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                      <User2 className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="truncate">
                        {requesterLabel(selectedCase)}
                      </span>
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Site
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                      <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="truncate">
                        {selectedCase.site?.name ?? "Entire workspace"}
                      </span>
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Opened
                    </p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {formatDate(selectedCase.createdAt)}
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Response target
                    </p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {selectedCase.firstResponseDueAt
                        ? formatDate(selectedCase.firstResponseDueAt)
                        : "Not set"}
                    </p>
                  </div>
                </div>

                {detailQuery.data?.linkedResources.length ? (
                  <div className="flex flex-wrap gap-2">
                    {detailQuery.data.linkedResources.map((resource) => (
                      <span
                        key={resource.id}
                        className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600"
                      >
                        {resource.label ||
                          resource.resourceType.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="space-y-4 rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.8),rgba(255,255,255,1))] p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                        Conversation
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-slate-950">
                        Shared thread history
                      </h3>
                    </div>
                    <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                      {detailQuery.data?.messages.length ?? 0} messages
                    </div>
                  </div>

                  {detailQuery.data?.messages.length ? (
                    <div className="space-y-3">
                      {detailQuery.data.messages.map((message) => (
                        <MessageBubble key={message.id} message={message} />
                      ))}
                    </div>
                  ) : (
                    <p className="py-4 text-center text-sm text-slate-400">
                      No messages yet.
                    </p>
                  )}
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                        Reply
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-slate-950">
                        Keep the workspace thread moving
                      </h3>
                    </div>
                    {selectedCase.status === "RESOLVED" ? (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Replying will reopen this case
                      </span>
                    ) : null}
                    {selectedCase.status === "CLOSED" ? (
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                        Closed cases are read-only
                      </span>
                    ) : null}
                  </div>

                  <textarea
                    rows={4}
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    placeholder="Add context, answer a question, or confirm next steps for your team."
                    disabled={selectedCase.status === "CLOSED"}
                    className="mt-4 w-full rounded-3xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100"
                  />

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs leading-5 text-slate-500">
                      Visible to everyone in{" "}
                      {session?.activeTenant?.name ?? "this workspace"}.
                    </p>
                    <button
                      type="button"
                      onClick={() => replyMutation.mutate()}
                      disabled={replyDisabled}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {replyMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {selectedCase.status === "RESOLVED"
                        ? "Reply and reopen"
                        : "Send reply"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
